import ELK from 'elkjs/lib/elk.bundled.js';

const elk = new ELK();

// ── Node dimensions by type ──
export const NODE_DIMS = {
  task:       { width: 240, height: 160 },
  gate:       { width: 180, height: 80 },
  join:       { width: 140, height: 40 },
  end:        { width: 140, height: 50 },
  parallel:   { width: 300, height: 200 },
  timer:      { width: 180, height: 70 },
  subprocess: { width: 280, height: 180 },
  swimlane:   { width: 600, height: 400 },
};

/**
 * Layout workflow nodes using elkjs.
 *
 * @param {Array} nodes - Flat node array [{id, type, parentId?, ...}]
 * @param {Array} edges - Edge array [{id, from, to, ...}]
 * @param {Object} options - Layout options
 * @returns {Promise<{nodes: Array, edges: Array}>} - Nodes with updated positions + edges with route points
 */
export async function elkLayout(nodes, edges, options = {}) {
  const {
    direction = 'DOWN', // 'DOWN' | 'RIGHT'
    spacing = { node: 60, layer: 80 },
    swimlanes = [],
  } = options;

  // Build the elk graph
  const elkGraph = buildElkGraph(nodes, edges, { direction, spacing, swimlanes });

  // Run layout
  const layoutResult = await elk.layout(elkGraph);

  // Map elk positions back to our node format
  const positionMap = extractPositions(layoutResult);

  const positionedNodes = nodes.map((n) => ({
    ...n,
    position: positionMap[n.id] || n.position || { x: 0, y: 0 },
  }));

  return { nodes: positionedNodes, edges };
}

/**
 * Build the elk graph structure from our flat nodes/edges.
 */
function buildElkGraph(nodes, edges, { direction, spacing, swimlanes }) {
  const elkDirection = direction === 'RIGHT' ? 'RIGHT' : 'DOWN';

  // Group nodes by parent
  const childrenOf = {};
  const rootNodes = [];
  nodes.forEach((n) => {
    if (n.parentId) {
      if (!childrenOf[n.parentId]) childrenOf[n.parentId] = [];
      childrenOf[n.parentId].push(n);
    } else {
      rootNodes.push(n);
    }
  });

  // If swimlanes are defined, group root nodes by swimlane
  const swimlaneMap = {};
  if (swimlanes.length > 0) {
    swimlanes.forEach((sl) => { swimlaneMap[sl.id] = []; });
    swimlaneMap.__unassigned = [];
  }

  function buildElkNode(node) {
    const dims = NODE_DIMS[node.type] || NODE_DIMS.task;
    const children = childrenOf[node.id] || [];

    const elkNode = {
      id: node.id,
      width: dims.width,
      height: dims.height,
      labels: [{ text: node.label || node.id }],
      // Port constraints for proper edge routing
      layoutOptions: {
        'org.eclipse.elk.portConstraints': 'FIXED_ORDER',
      },
    };

    // Add ports for handles
    elkNode.ports = [
      { id: `${node.id}-target`, properties: { 'org.eclipse.elk.port.side': 'NORTH' } },
      { id: `${node.id}-source`, properties: { 'org.eclipse.elk.port.side': 'SOUTH' } },
    ];

    // Gate nodes have multiple source ports
    if (node.type === 'gate' && node.options) {
      elkNode.ports = [
        { id: `${node.id}-target`, properties: { 'org.eclipse.elk.port.side': 'NORTH' } },
        ...node.options.map((opt, i) => ({
          id: `${node.id}-${opt.toLowerCase()}`,
          properties: { 'org.eclipse.elk.port.side': i === 0 ? 'WEST' : 'EAST' },
        })),
      ];
    }

    // Compound nodes (parallel, subprocess) contain children
    if (children.length > 0) {
      elkNode.children = children.map(buildElkNode);
      elkNode.layoutOptions = {
        ...elkNode.layoutOptions,
        'org.eclipse.elk.algorithm': 'layered',
        'org.eclipse.elk.direction': elkDirection,
        'org.eclipse.elk.spacing.nodeNode': String(spacing.node * 0.7),
        'org.eclipse.elk.layered.spacing.nodeNodeBetweenLayers': String(spacing.layer * 0.7),
        'org.eclipse.elk.hierarchyHandling': 'INCLUDE_CHILDREN',
        'org.eclipse.elk.padding': '[top=40,left=20,bottom=20,right=20]',
      };
      // Auto-size compound nodes
      delete elkNode.width;
      delete elkNode.height;
    }

    return elkNode;
  }

  // Build root children (possibly grouped by swimlane)
  let rootChildren;

  if (swimlanes.length > 0) {
    // Group nodes into swimlane containers
    rootNodes.forEach((n) => {
      const lane = n.swimlane || '__unassigned';
      if (swimlaneMap[lane]) {
        swimlaneMap[lane].push(n);
      } else {
        swimlaneMap.__unassigned.push(n);
      }
    });

    rootChildren = [];
    swimlanes.forEach((sl) => {
      const laneNodes = swimlaneMap[sl.id] || [];
      if (laneNodes.length === 0) return;

      rootChildren.push({
        id: `swimlane-${sl.id}`,
        labels: [{ text: sl.label }],
        children: laneNodes.map(buildElkNode),
        layoutOptions: {
          'org.eclipse.elk.algorithm': 'layered',
          'org.eclipse.elk.direction': elkDirection,
          'org.eclipse.elk.spacing.nodeNode': String(spacing.node),
          'org.eclipse.elk.layered.spacing.nodeNodeBetweenLayers': String(spacing.layer),
          'org.eclipse.elk.padding': '[top=50,left=30,bottom=20,right=30]',
        },
      });
    });

    // Unassigned nodes go at root level
    const unassigned = swimlaneMap.__unassigned || [];
    unassigned.forEach((n) => rootChildren.push(buildElkNode(n)));
  } else {
    rootChildren = rootNodes.map(buildElkNode);
  }

  // Build edges (filter loops for layout stability)
  const elkEdges = edges
    .filter((e) => !e.isLoop)
    .map((e) => ({
      id: e.id,
      sources: [e.from],
      targets: [e.to],
    }));

  return {
    id: 'root',
    layoutOptions: {
      'org.eclipse.elk.algorithm': 'layered',
      'org.eclipse.elk.direction': elkDirection,
      'org.eclipse.elk.spacing.nodeNode': String(spacing.node),
      'org.eclipse.elk.layered.spacing.nodeNodeBetweenLayers': String(spacing.layer),
      'org.eclipse.elk.edgeRouting': 'ORTHOGONAL',
      'org.eclipse.elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'org.eclipse.elk.hierarchyHandling': 'INCLUDE_CHILDREN',
    },
    children: rootChildren,
    edges: elkEdges,
  };
}

/**
 * Recursively extract node positions from elk layout result.
 */
function extractPositions(elkNode, offsetX = 0, offsetY = 0) {
  const positions = {};

  if (elkNode.children) {
    elkNode.children.forEach((child) => {
      const absX = offsetX + (child.x || 0);
      const absY = offsetY + (child.y || 0);

      // Only record position for real nodes (not swimlane containers)
      if (!child.id.startsWith('swimlane-')) {
        positions[child.id] = { x: absX, y: absY };
      }

      // Recurse into children (compound nodes, swimlanes)
      if (child.children) {
        const childPositions = extractPositions(child, absX, absY);
        Object.assign(positions, childPositions);
      }
    });
  }

  return positions;
}

/**
 * Simple fallback layout when elkjs async is not desired (e.g., initial render).
 * Uses a basic topological sort + grid placement.
 */
export function quickLayout(nodes, edges, cols = 3) {
  // Topo sort
  const dagEdges = edges.filter((e) => !e.isLoop);
  const inDegree = {};
  const adj = {};
  nodes.forEach((n) => { inDegree[n.id] = 0; adj[n.id] = []; });
  dagEdges.forEach((e) => {
    const from = e.from || e.source;
    const to = e.to || e.target;
    if (adj[from]) adj[from].push(to);
    inDegree[to] = (inDegree[to] || 0) + 1;
  });
  const queue = nodes.filter((n) => inDegree[n.id] === 0).map((n) => n.id);
  const sorted = [];
  while (queue.length > 0) {
    const id = queue.shift();
    sorted.push(id);
    (adj[id] || []).forEach((to) => {
      inDegree[to]--;
      if (inDegree[to] === 0) queue.push(to);
    });
  }
  // Add any unsorted nodes (cycles)
  nodes.forEach((n) => { if (!sorted.includes(n.id)) sorted.push(n.id); });

  const colGap = 320;
  const rowGap = 220;
  const marginX = 60;
  const marginY = 40;

  const positions = {};
  sorted.forEach((id, i) => {
    const row = Math.floor(i / cols);
    const colIndex = i % cols;
    const col = row % 2 === 0 ? colIndex : (cols - 1 - colIndex);
    positions[id] = {
      x: marginX + col * colGap,
      y: marginY + row * rowGap,
    };
  });

  return nodes.map((n) => ({
    ...n,
    position: positions[n.id] || { x: 0, y: 0 },
  }));
}
